import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, OnInit, signal, output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { InfoGroupComponent } from '../../../../shared/components/info-group/info-group.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { UserAvatarComponent } from '../../../../shared/components/user-avatar/user-avatar.component';
import { StatusSeverity } from '../../../../shared/custom/statusSeverity';
import { RideDocumentDto } from '../../../../shared/interfaces/ride-firestore';
import { RideListItem } from '../../../../shared/interfaces/ride-list-item';
import { ToggleMenuService } from '../../../../shared/services/toggle-menu.service';
import { MenuItem, MessageService, TooltipOptions } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Menu, MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ActionDialogService } from '../../services/action-dialog.service';
import { DriverDetailsCacheService } from '../../services/driver-details-cache.service';
import { MapRideServiceService } from '../../services/map-ride-service.service';
import { RideActionsService } from '../../services/ride-actions.service';
import { ActiveStepPipe } from './active-step.pipe';
import { Common, EquipmentsNeed } from '../../../../shared/enums/common';
import { EnvConfigService } from '../../../../shared/services/env-config.service';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { UserRole } from '../../../../core/auth/enums/userRoles';
import { CameraStatus, CameraStatusService } from '../../services/camera-status.service';
import { StudentsListDialogComponent } from './students-list-dialog/students-list-dialog.component';
import { RideUpdatesService } from '../../services/ride-updates.service';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { isCameraDialogEnabled as checkCameraDialogEnabled } from '../../../../shared/utilities/utility-functions';

@Component({
  selector: 'app-ride-item',
  standalone: true,
  imports: [CommonModule, ActiveStepPipe, ButtonComponent, InfoGroupComponent, StepperComponent, TagComponent, UserAvatarComponent, MenuModule, TooltipModule, DialogModule, ButtonModule],
  providers: [DialogService],
  templateUrl: './ride-item.component.html',
  styleUrl: './ride-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RideItemComponent implements OnInit {
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly mapRideService = inject(MapRideServiceService);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly rideActionsService = inject(RideActionsService);
  private readonly toggleMenuService = inject(ToggleMenuService);
  private readonly driverDetailsCacheService = inject(DriverDetailsCacheService);
  private readonly env = inject(EnvConfigService);
  private readonly authService = inject(AuthService);
  private readonly cameraStatusService = inject(CameraStatusService);
  private readonly rideUpdatesService = inject(RideUpdatesService);

  ride = input.required<RideDocumentDto>();
  // Live camera status (service-driven)
  cameraId = signal<string>('');
  cameraOnlineStatus = signal<boolean>(false);
  lastSyncDate = signal<Date | null>(null);
  index = input<number>(1);
  mappedRide;
  statusSeverity: StatusSeverity = 'info'; //default
  activeRide = input<boolean>(false);
  isPendingRides = input<boolean>(false);

  partnerBotId = signal(Common.PartnerBotId);
  appSetting = this.authService.appSetting();
  equipmentsNeed = EquipmentsNeed;

  tooltipOptions: TooltipOptions = {
    showDelay: 250,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };
  driverAvailability = signal<boolean>(false);
  monitorAvailability = signal<boolean>(false);
  driverImg = signal<string>('');

  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  userRole = UserRole;
  public showCameraDailog: boolean = false;
  menuItems: MenuItem[] | undefined = [];
  readonly viewRide = output<RideListItem>();
  // Computed signal to determine if camera dialog should be enabled based on ride status and time
  isCameraDialogEnabled = computed(() => {
    const mappedRide = this.mappedRide();
    return checkCameraDialogEnabled(mappedRide, mappedRide?.JobStatusId);
  });

  constructor() {
    this.mappedRide = computed(() => {
      const ride = this.ride();
      this.mapRideService.TimezoneDropdownValue();

      const mappedRide = this.mapRideService.getMappedItem(ride);
      this.setMenu(mappedRide);
      return mappedRide;
    });

    // Emit updates only for the active ride being tracked
    effect(() => {
      const currentMappedRide = this.mappedRide();
      if (currentMappedRide?.id) {
        this.rideUpdatesService.emitRideUpdate(String(currentMappedRide.id), currentMappedRide);
      }
    });
  }

  ngOnInit() {
    this.fetchData();

    // Subscribe to live camera status by driver id (hide when PartnerBot/no driver)
    const driverId = this.mappedRide()?.driverDetails?.id;
    if (driverId && driverId != this.partnerBotId()) {
      this.cameraStatusService.watchStatus(driverId).subscribe((status: CameraStatus) => {
        this.cameraId.set(status.camera_id || '');
        this.cameraOnlineStatus.set(status.camera_online_status || false);
        this.lastSyncDate.set(status.last_sync_date || null);
      });
    }
  }

  private fetchData() {
    const ride = this.ride();
    const driver = ride?.ride_drivers?.find(d => !d.is_deleted);
    if (!driver?.id) return;

    this.getPartnerAvailability(driver.driver_id ?? 0);

    const monitor = ride.ride_cargoes?.find(x => !x.is_deleted && x.is_attendant);
    if (monitor) {
      this.getMonitorAvailability(monitor.assistant_id ?? 0);
    }

    const driverImg = driver.ride_driver_extra_fields?.find(f => f.key_id == 6)?.value;
    this.setUserImg(driverImg ?? '', driver.id);
  }

  private getPartnerAvailability(partnerId: number) {
    this.driverDetailsCacheService.getAvailabilityStatus(partnerId).subscribe(res => {
      this.driverAvailability.update(prev => res);
    });
  }

  private getMonitorAvailability(monitorId: number) {
    this.driverDetailsCacheService.getAvailabilityStatus(monitorId).subscribe(res => {
      console.log(res);
      this.monitorAvailability.update(prev => res);
    });
  }

  private setUserImg(driverImg: string, driverId: number) {
    this.driverDetailsCacheService.getImageUrl(driverImg, driverId)?.subscribe(res => {
      this.driverImg.update(prev => res);
    });
  }

  setMenu(mappedRide: RideListItem) {
    const isPending = this.isPendingRides();
    this.menuItems = [
      {
        label: 'Send Ride Offer',
        visible: this.rideActionsService.viewSendRideOffer(isPending),
        disabled: this.rideActionsService.disableSendRideOffer(mappedRide),
        command: e => {
          this.rideActionsService.openSendRideOfferDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Auto Accept Ride',
        visible: this.rideActionsService.viewAutoAcceptRideButton(mappedRide, isPending),
        command: e => {
          this.rideActionsService.openAutoAcceptRideDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Auto Start Ride',
        visible: this.rideActionsService.viewAutoStartRideButton(mappedRide, isPending),
        command: e => {
          this.rideActionsService.openAutoStartRideDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Reset Ride',
        visible: this.rideActionsService.viewResetRide(isPending),
        disabled: this.rideActionsService.disableResetRide(mappedRide),
        command: e => {
          this.rideActionsService.openResetRideDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Driver Information',
        visible: !isPending && mappedRide.driverDetails?.id != this.partnerBotId() && (localStorage.getItem('role') ?? '')?.toLowerCase() == UserRole.Client,
        command: e => {
          this.openContractorDialog();
        },
      },
      {
        label: 'View Ride Activity',
        visible: this.rideActionsService.viewRideActivity(isPending),
        command: e => {
          this.rideActionsService.openRideActivityDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: 'Change Ride Time',
        visible: this.rideActionsService.viewChangeRideTimeButton(mappedRide, isPending),
        command: e => {
          this.rideActionsService.openChangeRideTimeDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: 'Complete Ride',
        visible: this.rideActionsService.viewCompleteRide(),
        disabled: !isPending || mappedRide.JobStatusId == RideStatusType.Completed || mappedRide.JobStatusId == RideStatusType.NoShow || !mappedRide.driverDetails?.id || mappedRide.driverDetails?.id == this.partnerBotId(),
        command: e => {
          this.rideActionsService.openCompleteRideDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: (mappedRide.rideInfo.TotalBillableWaitingTimeInMinutes ?? 0) > 0 ? 'Edit Waiting Time' : 'Add Waiting Time',
        visible: this.rideActionsService.viewWaitingTimeButton(mappedRide, isPending),
        command: e => {
          this.rideActionsService.openWaitingTimeDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: 'Delete Ride',
        visible: this.rideActionsService.viewDeleteRide(),
        command: e => {
          this.rideActionsService.openDeleteRideDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        separator: true,
        visible:
          !isPending &&
          (this.rideActionsService.viewIgnoreDoubleBooked(isPending) ||
            this.rideActionsService.viewIgnoreMonitorDoubleBooked(isPending) ||
            this.rideActionsService.viewAssignSubDriverButton(mappedRide, isPending) ||
            this.rideActionsService.viewUnassignDriver(isPending) ||
            this.rideActionsService.viewFindDriverButton(mappedRide, isPending)),
      },
      {
        label: 'Ignore Driver 2X Double Booked',
        visible: this.rideActionsService.viewIgnoreDoubleBooked(isPending),
        disabled: this.rideActionsService.disableIgnoreDoubleBooked(mappedRide),
        command: e => {
          this.rideActionsService.openIgnoreDriverDoubleBookedDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Ignore Monitor 2X Double Booked',
        visible: this.rideActionsService.viewIgnoreMonitorDoubleBooked(isPending),
        disabled: this.rideActionsService.disableIgnoreMonitorDoubleBooked(mappedRide),
        command: e => {
          this.rideActionsService.openIgnoreMonitorDoubleBookedDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Un-assign Driver',
        visible: this.rideActionsService.viewUnassignDriver(isPending),
        disabled: this.rideActionsService.disableUnassignDriver(mappedRide),
        command: e => {
          this.rideActionsService.openUnassignDriverDialog(mappedRide, this.dialogService, this.messageService);
        },
      },
      {
        label: 'Assign Sub Driver',
        visible: this.rideActionsService.viewAssignSubDriverButton(mappedRide, isPending),
        command: e => {
          if (this.currentRole == UserRole.Provider) {
            this.rideActionsService.openProviderAssignSubDriverDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
          } else {
            this.rideActionsService.openAssignSubDriverDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
          }
        },
      },
      {
        label: 'Find Driver',
        visible: this.rideActionsService.viewFindDriverButton(mappedRide, isPending),
        command: e => {
          this.rideActionsService.openFindDriverDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        separator: true,
        visible: !isPending && (this.rideActionsService.viewAddIncidentReport(isPending) || this.rideActionsService.viewAddPayoutAdjustmentButton(mappedRide, isPending) || this.rideActionsService.viewSendDelayNotification(isPending)),
      },
      {
        label: 'Add Incident Report',
        visible: this.rideActionsService.viewAddIncidentReport(isPending),
        command: e => {
          this.rideActionsService.openIncidentReportDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: 'Add Payout Adjustment',
        visible: this.rideActionsService.viewAddPayoutAdjustmentButton(mappedRide, isPending),
        command: e => {
          this.rideActionsService.openPayoutAdjustmentDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: 'Send Delay Notification',
        visible: this.rideActionsService.viewSendDelayNotification(isPending),
        command: e => {
          this.rideActionsService.openDelayNotificationDialog(mappedRide, this.ride(), this.dialogService, this.messageService);
        },
      },
      {
        label: 'Cancel Ride',
        visible: this.rideActionsService.viewCancelRide(mappedRide),
        command: e => {
          this.rideActionsService.openMassCancelRidesDialog(this.dialogService, this.messageService, mappedRide, this.ride(), true, isPending);
        },
      },
    ];
  }

  toggleRide(ride: RideListItem) {
    console.log('%cride --> toggled', 'color: blue;', ride);
    if (this.activeRide()) {
      if (this.env.getConfig().isRunAsLibrary) {
        this.router.navigateByUrl(`/dispatch/monitoring-center/rides`);
      } else {
        this.router.navigateByUrl(`/monitoring-center/rides`);
      }
    } else {
      this.viewRide.emit(ride);
      if (this.env.getConfig().isRunAsLibrary) {
        this.router.navigateByUrl(`/dispatch/monitoring-center/ride-details/${ride.id}`);
      } else {
        this.router.navigateByUrl(`/monitoring-center/ride-details/${ride.id}`);
      }
    }
  }

  openStudentsListDialog(studentNames: string[]) {
    this.dialogService.open(StudentsListDialogComponent, {
      showHeader: false,
      width: '640px',
      data: {
        studentNames,
      },
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    });
  }

  openMenu(event: Event, menu: Menu) {
    this.toggleMenuService.openMenu(menu, event);
  }

  onHideMenu(e: any, item: HTMLDivElement) {
    item.classList.remove('menu-opened');
  }
  onShowMenu(e: any, item: HTMLDivElement) {
    item.classList.add('menu-opened');
  }

  openMessagingDialog(field: 'guardian' | 'contractor', allMessages: boolean, ride: RideListItem) {
    this.actionDialogService.openMessagingDialog(this.dialogService, field, allMessages, ride.id.toString());
  }

  openContractorDialog(isMonitor = false) {
    this.actionDialogService.openContractorDialog(this.dialogService, this.messageService, this.mappedRide(), this.ride(), isMonitor);
  }

  openEquipmentDialog() {
    this.actionDialogService.openPassengersEquipmentsDialog(this.dialogService, this.messageService, this.ride());
  }

  openBlueprintDialog() {
    this.actionDialogService.openBlueprintDialog(this.dialogService, this.mappedRide(), this.ride());
  }

  openMapDialog(ride: RideListItem) {
    const mapDialogData = {
      dialogService: this.dialogService,
      docRide: this.ride(),
      mappedRide: ride,
      rideId: String(ride.id), // Pass ride ID to subscribe to updates
      driverOnline: this.driverAvailability(),
    };
    this.actionDialogService.openMapDialog(mapDialogData);
  }

  openRideActivityDialog(ride: RideListItem) {
    this.rideActionsService.openRideActivityDialog(ride, this.ride(), this.dialogService, this.messageService);
  }

  public openCameraDialog(cameraOnlineStatus: boolean, lastSyncDate: Date | null) {
    const driver = this.ride()?.ride_drivers?.find(d => !d.is_deleted);
    this.actionDialogService.openCameraDialog(driver?.driver_id ?? 0, cameraOnlineStatus, lastSyncDate, this.cameraStatusService, this.dialogService, this.mappedRide());
  }
}
